import { Text } from '@customerio/pluma-components/react';
import { isTwoValueFilterOperator, isUnaryFilterOperator } from '../../attribute-filter-state';
import { isOneOfOperator } from '../../operators';
import {
  isAbsoluteTimestampOperator,
  isRelativeTimestampOperator,
  isTimestampInequalityOperator,
} from '../../relative-timestamp';
import { OneOfValueSelect } from './one-of-value-select';
import { AbsoluteTimestampValue, RelativeTimestampValue, TimestampRangeValue } from './timestamp-editors';
import { ValueCombobox } from './value-combobox';
import type { ConditionControlSize, OperatorValuePatch, ValueOption } from './types';

export type ValueEditorProps = {
  field: string;
  operator: string;
  value: string | null;
  value2: string | null;
  valueOptions: ValueOption[];
  isLoadingValueOptions?: boolean;
  disabled?: boolean;
  valueError?: string;
  /** The second value's error, for an operator that takes two. */
  value2Error?: string;
  /** What the value field says while empty; "Value" by default. */
  valuePlaceholder?: string;
  onChange: (patch: OperatorValuePatch) => void;
  onValueBlur: () => void;
  size?: ConditionControlSize;
};

/**
 * The value half of a condition row — whichever editor the operator needs.
 *
 * A unary operator (`exists`) renders nothing: it takes no value, and the one
 * it may carry off the wire is never shown.
 */
export function ValueEditor({
  field,
  operator,
  value,
  value2,
  valueOptions,
  isLoadingValueOptions,
  disabled,
  valueError,
  value2Error,
  valuePlaceholder,
  onChange,
  onValueBlur,
  size = 'sm',
}: ValueEditorProps) {
  if (isUnaryFilterOperator(operator)) {
    return null;
  }

  if (operator === 'timestamp_bw' || isRelativeTimestampOperator(operator) || isAbsoluteTimestampOperator(operator)) {
    return (
      <TimestampValueEditor
        field={field}
        operator={operator}
        value={value}
        value2={value2}
        disabled={disabled}
        valueError={valueError}
        value2Error={value2Error}
        onChange={onChange}
        size={size}
      />
    );
  }

  // A timestamp inequality with no variant resolved yet renders only its
  // variant select, which the operator row owns.
  if (isTimestampInequalityOperator(operator)) {
    return null;
  }

  if (isOneOfOperator(operator)) {
    return (
      <OneOfValueSelect
        disabled={disabled}
        value={value}
        valueOptions={valueOptions}
        isLoadingValueOptions={isLoadingValueOptions}
        error={valueError}
        onChange={onChange}
        onTouched={onValueBlur}
        size={size}
      />
    );
  }

  if (isTwoValueFilterOperator(operator)) {
    return (
      <TwoValueEditor
        value={value}
        value2={value2}
        valueOptions={valueOptions}
        isLoadingValueOptions={isLoadingValueOptions}
        disabled={disabled}
        valueError={valueError}
        value2Error={value2Error}
        valuePlaceholder={valuePlaceholder}
        onChange={onChange}
        onValueBlur={onValueBlur}
        size={size}
      />
    );
  }

  return (
    <ValueCombobox
      ariaLabel="Value"
      testId="dryad-filter-value-input"
      placeholder={valuePlaceholder}
      disabled={disabled}
      inputValue={value}
      isLoadingValueOptions={isLoadingValueOptions}
      valueOptions={valueOptions}
      error={valueError}
      onValueChange={(nextValue) => onChange({ value: nextValue })}
      onBlur={onValueBlur}
      size={size}
    />
  );
}

type TwoValueEditorProps = Omit<ValueEditorProps, 'field' | 'operator'>;

/** The two values a `bw` condition holds, each with its own error. */
function TwoValueEditor({
  value,
  value2,
  valueOptions,
  isLoadingValueOptions,
  disabled,
  valueError,
  value2Error,
  valuePlaceholder,
  onChange,
  onValueBlur,
  size = 'sm',
}: TwoValueEditorProps) {
  return (
    <>
      <ValueCombobox
        ariaLabel="Value"
        testId="dryad-filter-value-input"
        placeholder={valuePlaceholder}
        disabled={disabled}
        inputValue={value}
        isLoadingValueOptions={isLoadingValueOptions}
        valueOptions={valueOptions}
        error={valueError}
        onValueChange={(nextValue) => onChange({ value: nextValue })}
        onBlur={onValueBlur}
        size={size}
      />
      <Text as="span" color="secondary">
        and
      </Text>
      <ValueCombobox
        ariaLabel="Second value"
        disabled={disabled}
        inputValue={value2}
        isLoadingValueOptions={isLoadingValueOptions}
        valueOptions={valueOptions}
        error={value2Error}
        onValueChange={(nextValue) => onChange({ value2: nextValue })}
        size={size}
      />
    </>
  );
}

type TimestampValueEditorProps = Pick<
  ValueEditorProps,
  'field' | 'operator' | 'value' | 'value2' | 'disabled' | 'valueError' | 'value2Error' | 'onChange' | 'size'
>;

/** The editor for a timestamp operator: a range, a relative offset, or an absolute date. */
function TimestampValueEditor({
  field,
  operator,
  value,
  value2,
  disabled,
  valueError,
  value2Error,
  onChange,
  size = 'sm',
}: TimestampValueEditorProps) {
  if (operator === 'timestamp_bw') {
    return (
      <TimestampRangeValue
        disabled={disabled}
        value={value}
        value2={value2}
        error={valueError}
        error2={value2Error}
        onChange={onChange}
        size={size}
      />
    );
  }

  if (isRelativeTimestampOperator(operator)) {
    return (
      <RelativeTimestampValue
        disabled={disabled}
        field={field}
        operator={operator}
        value={value}
        error={valueError}
        onChange={onChange}
        size={size}
      />
    );
  }

  return (
    <AbsoluteTimestampValue disabled={disabled} value={value} error={valueError} onChange={onChange} size={size} />
  );
}
